import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpRequest, HttpEventType, HttpResponse } from '@angular/common/http';
import { Observable, filter, map } from 'rxjs';
import { AppUrlConstants } from '../constants/AppUrlConstants';
import { TokenStorageService } from './token-storage.service';
import {
  InternalChatRequest,
  InternalChatResponse,
  InternalChatApplication,
  ApplicationChatResponse,
  InternalChatLog,
  InternalDocument
} from '../models/internal-chatbot.model';

const INTERNAL_API_KEY = '9fK7#Lm2@Qx8!Vz4$Wp1^Rs6&Ty0*Ub3%Je5?Nd7+Hg9=Ka2';

@Injectable({
  providedIn: 'root'
})
export class InternalChatbotService {

  private get headers(): HttpHeaders {
    return new HttpHeaders({
      'X-API-Key': INTERNAL_API_KEY,
      'X-Skip-Spinner': 'true'
    });
  }

  constructor(private http: HttpClient, private tokenService: TokenStorageService) {}

  // PGML has no JWT user context (API-key only) — the acting user is supplied
  // by the frontend for history attribution
  private get currentUserId(): string {
    return String(this.tokenService.getUserId() ?? '');
  }

  private get currentUserName(): string {
    return this.tokenService.getEmail() || '';
  }

  // ── Chat ──────────────────────────────────────────────────────────────────

  askQuestion(request: InternalChatRequest): Observable<InternalChatResponse> {
    return this.http.post<InternalChatResponse>(
      AppUrlConstants.INTERNAL_CHAT_ASK_URL,
      request,
      { headers: this.headers }
    );
  }

  /** Actual GRMIS citizen-widget path. Java applies the governed shared-skill
   * adapter first and preserves the established FAQ provider as a fallback. */
  askCitizenQuestion(request: InternalChatRequest): Observable<InternalChatResponse> {
    return this.http.post<InternalChatResponse>(
      AppUrlConstants.host + AppUrlConstants.api + AppUrlConstants.v1 + AppUrlConstants.CHAT_BOT,
      request
    );
  }

  getEnabledApplications(): Observable<InternalChatApplication[]> {
    return this.http.get<InternalChatApplication[]>(AppUrlConstants.INTERNAL_CHAT_APPLICATIONS_URL);
  }

  askApplicationQuestion(request: {
    applicationCode: string; sessionId: string; message: string; language?: string; hermesMode?: string;
  }): Observable<ApplicationChatResponse> {
    return this.http.post<ApplicationChatResponse>(AppUrlConstants.INTERNAL_CHAT_MESSAGES_URL, request);
  }

  getAllLogs(): Observable<InternalChatLog[]> {
    return this.http.get<InternalChatLog[]>(
      AppUrlConstants.INTERNAL_CHAT_LOGS_URL,
      { headers: this.headers }
    );
  }

  getSessionLogs(sessionId: string): Observable<InternalChatLog[]> {
    return this.http.get<InternalChatLog[]>(
      `${AppUrlConstants.INTERNAL_CHAT_LOGS_URL}/${sessionId}`,
      { headers: this.headers }
    );
  }

  // ── Documents ─────────────────────────────────────────────────────────────

  getDocuments(): Observable<InternalDocument[]> {
    return this.http.get<InternalDocument[]>(
      AppUrlConstants.INTERNAL_DOCUMENTS_URL,
      { headers: this.headers }
    );
  }

  /**
   * Upload one or more PDFs. The backend stores the bytes and returns
   * immediately with PROCESSING tasks — chunking/embedding runs async on the
   * server; progress is tracked on the Documents History screen.
   */
  uploadDocuments(files: File[], onProgress: (pct: number) => void): Observable<any> {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    if (this.currentUserId) formData.append('createdBy', this.currentUserId);
    if (this.currentUserName) formData.append('createdByName', this.currentUserName);

    const req = new HttpRequest<FormData>('POST', AppUrlConstants.INTERNAL_DOCUMENT_UPLOAD_URL, formData, {
      headers: this.headers,
      reportProgress: true,
    });

    return this.http.request<any>(req).pipe(
      map(event => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
        return event;
      }),
      filter((event): event is HttpResponse<any> => event instanceof HttpResponse),
      map(event => event.body!)
    );
  }

  deleteDocument(documentId: string): Observable<void> {
    let params = new HttpParams();
    if (this.currentUserId) params = params.set('createdBy', this.currentUserId);
    if (this.currentUserName) params = params.set('createdByName', this.currentUserName);
    return this.http.delete<void>(
      `${AppUrlConstants.INTERNAL_DOCUMENTS_URL}/${documentId}`,
      { headers: this.headers, params }
    );
  }

  // ── Documents training history ────────────────────────────────────────────

  getDocTrainingHistory(params: {
    page: number; size: number;
    status?: string; dateFrom?: string; dateTo?: string; createdBy?: string;
  }): Observable<any> {
    let httpParams = new HttpParams()
      .set('page', String(params.page))
      .set('size', String(params.size));
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.dateFrom) httpParams = httpParams.set('dateFrom', params.dateFrom);
    if (params.dateTo) httpParams = httpParams.set('dateTo', params.dateTo);
    if (params.createdBy) httpParams = httpParams.set('createdBy', params.createdBy);

    return this.http.get<any>(AppUrlConstants.INTERNAL_TRAINING_HISTORY_URL, {
      headers: this.headers, params: httpParams
    });
  }

  retryDocTask(historyId: number): Observable<any> {
    return this.http.post<any>(
      `${AppUrlConstants.INTERNAL_TRAINING_HISTORY_URL}/${historyId}/retry`,
      {},
      { headers: this.headers }
    );
  }
}
